import React, { createContext, useEffect, useRef, useState } from 'react';
import { FullPageLoader } from 'src/components';

/* Used for caching values that don't change frequently, and take time to compute or are computationally expensive to recompute. */
export interface ICacheStore {
  set: (key: string, value: any, timeToLive?: number) => void;
  get: (key) => any | undefined;
  clear: () => void;
  remove: (key: string) => void;
}

/* Provides an ICacheStore instance to children. */
export const CacheContext = createContext<ICacheStore>({
  clear(): void {},
  get(): any {},
  set(): void {},
  remove(): void {},
});

export function CacheContextProvider(props: { cacheKey: string; children }) {
  const cacheStoreRef = useRef<Map<string, CachedValueWrapper>>(new Map());
  const [isCacheStoreReady, setIsCacheStoreReady] = useState<boolean>(false);

  const clearCacheStore = () => {
    cacheStoreRef.current.clear();
    sessionStorage.removeItem(props.cacheKey);
  };

  const saveCacheStore = () => {
    const cacheString = JSON.stringify(Object.fromEntries(cacheStoreRef.current), dateSerializer);
    sessionStorage.setItem(props.cacheKey, cacheString);
  };

  const removeCacheItem = (key: string) => {
    cacheStoreRef.current.delete(key);
    saveCacheStore();
  };

  useEffect(() => {
    const cacheString = sessionStorage.getItem(props.cacheKey);
    if (cacheString) {
      const cacheObject = cacheString ? JSON.parse(cacheString, dateDeserializer) : {};
      cacheStoreRef.current = new Map(Object.entries(cacheObject));
    }
    setIsCacheStoreReady(true);
  }, []);

  /**
   *  adds a value to the cache
   *  @param props.key key used to look up the value
   *  @param props.value the value to be cached
   *  @param props.timeToLive optional duration in milliseconds that the value should persist for
   **/
  const setCachedValue = (key: string, value: any, timeToLive?: number) => {
    cacheStoreRef.current.set(key, {
      item: value,
      timeExpire: timeToLive ? new Date(Date.now() + timeToLive) : undefined,
    });
    saveCacheStore();
  };

  /**
   *  returns a cached value
   *  @param props.key key used to look up the value
   *  @return the cached value (if it exists or has not expired)
   **/
  const getCachedValue = (key): any | undefined => {
    const cachedValue = cacheStoreRef.current.get(key);

    if (cachedValue?.timeExpire) {
      const now = new Date();

      if (now.getTime() > cachedValue.timeExpire.getTime()) {
        return undefined;
      }
    }

    return cachedValue?.item;
  };

  const cacheStore: ICacheStore = {
    set: (key: string, value: any, timeToLive?: number) => setCachedValue(key, value, timeToLive),
    get: (key) => getCachedValue(key),
    clear: () => clearCacheStore(),
    remove: (key: string) => removeCacheItem(key),
  };

  return isCacheStoreReady ? (
    <CacheContext.Provider value={cacheStore}>{props.children}</CacheContext.Provider>
  ) : (
    <FullPageLoader showLoader={true} />
  );
}

function isDate(value: string) {
  return /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}(?:\.\d*))(?:Z|(\+|-)([\d|:]*))?$/.exec(
    value,
  );
}

var dateDeserializer = function (key, value) {
  if (typeof value !== 'string') {
    return value;
  }

  if (isDate(value)) {
    return new Date(value);
  }

  return value;
};

var dateSerializer = function (key, value) {
  if (key instanceof Date) {
    return key.toUTCString();
  }
  return value;
};

type CachedValueWrapper = {
  item: any;
  timeExpire?: Date;
};
